import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    ChevronDown,
    ChevronRight,
    CheckCircle2,
    Users,
    MessageSquare,
} from "lucide-react";
import type { GmailThreadPreview } from "@/hooks/use-gmail";
import { formatEmailDate } from "./email-format";
import { MessageRow } from "./MessageRow";

interface ThreadRowProps {
    thread: GmailThreadPreview;
    isExpanded: boolean;
    isSelected: boolean;
    isMessageSelected: (messageId: string) => boolean;
    onToggleExpand: () => void;
    onToggleThread: () => void;
    onToggleMessage: (messageId: string) => void;
    disabled: boolean;
}

/**
 * A selectable, expandable Gmail thread in the import modal. The thread-level
 * checkbox selects the whole thread; expanding reveals per-message rows.
 */
export function ThreadRow({
    thread,
    isExpanded,
    isSelected,
    isMessageSelected,
    onToggleExpand,
    onToggleThread,
    onToggleMessage,
    disabled,
}: ThreadRowProps) {
    const importableMessages = thread.messages.filter((m) => !m.alreadyImported);
    const allImported = importableMessages.length === 0;

    return (
        <div
            className={`border rounded-lg overflow-hidden transition-colors w-full min-w-0 ${
                isSelected
                    ? "border-primary bg-primary/5"
                    : allImported
                      ? "border-muted bg-muted/30 opacity-60"
                      : "border-border hover:border-muted-foreground/30"
            }`}
        >
            {/* Thread header */}
            <div className="flex items-start gap-3 p-3 w-full min-w-0 overflow-hidden">
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggleThread()}
                    disabled={disabled || allImported}
                    className="mt-0.5 shrink-0"
                />
                <Collapsible open={isExpanded} onOpenChange={onToggleExpand} className="flex-1 min-w-0 overflow-hidden">
                    <CollapsibleTrigger className="w-full text-left min-w-0">
                        <div className="flex items-start justify-between gap-2 min-w-0">
                            <div className="min-w-0 flex-1 overflow-hidden">
                                <div className="flex items-center gap-2 mb-1 min-w-0 overflow-hidden">
                                    <span className="text-sm font-medium line-clamp-2 break-all block min-w-0">
                                        {thread.subject}
                                    </span>
                                    {allImported && (
                                        <Badge variant="secondary" className="text-[10px] h-5 shrink-0">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            Already imported
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-1 break-all mb-1">
                                    {thread.snippet}
                                </p>
                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <Users className="h-3 w-3" />
                                        {thread.participants.length} participant{thread.participants.length !== 1 ? "s" : ""}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <MessageSquare className="h-3 w-3" />
                                        {thread.messageCount} message{thread.messageCount !== 1 ? "s" : ""}
                                    </span>
                                    {thread.lastMessageDate && (
                                        <span>{formatEmailDate(thread.lastMessageDate)}</span>
                                    )}
                                </div>
                            </div>
                            <div className="shrink-0 mt-1">
                                {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                )}
                            </div>
                        </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="w-0 min-w-full overflow-hidden">
                        <Separator className="my-2" />
                        <div className="space-y-1.5 w-0 min-w-full overflow-hidden">
                            {thread.messages.map((msg) => (
                                <MessageRow
                                    key={msg.messageId}
                                    message={msg}
                                    isSelected={isMessageSelected(msg.messageId)}
                                    onToggle={() => onToggleMessage(msg.messageId)}
                                    disabled={disabled || msg.alreadyImported}
                                />
                            ))}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>
        </div>
    );
}
